import * as THREE from 'three/webgpu';

import { convexHull2D, offsetPolygon, triangulatePolygon, rectToPoly } from '../build/polygon.js';
import { decalMaterial, RENDER_ORDER } from '../render/materials.js';

/**
 * @file The ground DecalLayer: contact ribbons, projected hard sun shadows,
 * crosswalks, burn patches — everything painted ON the ground rather than baked
 * INTO it. One merged mesh per level, one draw call, no shadow maps anywhere.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SHADOW TRICK
 * ─────────────────────────────────────────────────────────────────────────────
 * The sun is a LEVEL CONSTANT, so a box's shadow is just
 * `convexHull2D(footprint ∪ footprint + h * (-L.xz / L.y))` — exact, crisp, and
 * free. `skirt` insets a soft fade band inside the hull so the edge is not a
 * razor line (0.8 m reads as penumbra at gameplay scale without going mushy).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NON-NEGOTIABLES (learned the hard way, see render/materials.js)
 * ─────────────────────────────────────────────────────────────────────────────
 * • `decalMaterial()` reads a Float32 itemSize-1 attribute named exactly
 *   `alpha`. No attribute, no decals — it renders fully transparent and warns
 *   once.
 * • The layer is FrontSide with +Y normals, and a polygon wound CCW in (x,z)
 *   (positive shoelace) faces -Y. Every triangle here is emitted through
 *   {@link pushTri}, which checks the signed area and swaps two corners rather
 *   than trusting the caller's winding.
 * • +0.015 m Y lift, NO polygonOffset — WebGPU's polygon-offset semantics differ
 *   from WebGL's and the lift is portable.
 * • Draped over a heightfield: subdivide to <= 1.5 m edges, then sample
 *   `heightAt` per vertex and lift +0.02.
 * • ...UNLESS the vertex names its own Y. `pushTri`'s optional `y` argument is
 *   an ABSOLUTE world height and it wins over both the flat plane and the
 *   drape — it is how paint that belongs on a surface the sampler does not
 *   describe (a country road's ribbon, floating over its own carved corridor)
 *   stays glued to it. See {@link DecalBuilder#build}.
 *
 * @module engine/bake/decals
 */

/** @typedef {[number, number]} P2 */
/** @typedef {P2[]} Poly */
/** @typedef {import('../types.js').DecalLayer} DecalLayer */
/**
 * A flat triangle soup on the ground plane. 3 vertices per triangle; `xz` holds
 * 2 floats per vertex, `a` (alpha) and `uv` follow the same vertex order.
 *
 * `y` is OPTIONAL, per vertex, and absent on every soup that does not ask for
 * it — see {@link pushTri}'s `y` argument and the "PAINT THAT KNOWS ITS OWN
 * HEIGHT" note on {@link DecalBuilder#build}.
 * @typedef {{xz: number[], a: number[], uv: number[], y?: number[]}} DecalSoup
 */

const EPS = 1e-9;

/** @returns {DecalSoup} */
export function emptySoup() {
  return { xz: [], a: [], uv: [] };
}

/**
 * Materialises the optional per-vertex Y channel, back-filling `n` NaNs for the
 * vertices that were pushed before anybody asked for one. NaN is the "no
 * opinion, use the layer's plane or the drape" sentinel — a soup that never
 * calls this keeps no `y` array at all, which is what makes every existing
 * caller byte-identical.
 * @param {DecalSoup} s @param {number} n @returns {number[]}
 */
function yChannel(s, n) {
  if (!s.y) s.y = new Array(n).fill(NaN);
  while (s.y.length < n) s.y.push(NaN);
  return s.y;
}

/**
 * Appends one triangle with the winding fixed so it faces +Y.
 * @param {DecalSoup} s
 * @param {number} ax @param {number} az @param {number} aa
 * @param {number} bx @param {number} bz @param {number} ba
 * @param {number} cx @param {number} cz @param {number} ca
 * @param {number[]|null} [uv] 6 numbers `[u0,v0,u1,v1,u2,v2]`; defaults to the
 *   texture centre so a mapped layer does not sample its own edge.
 * @param {number[]|number|null} [y] ABSOLUTE world Y per vertex (`[ya,yb,yc]`,
 *   or one number for all three). Omit it — the normal case — and the vertex
 *   takes the layer's flat plane or its drape. ⚠ The winding fix swaps b and c,
 *   and this swaps with them; that is the whole reason it is an argument here
 *   rather than something the caller pushes itself.
 */
export function pushTri(s, ax, az, aa, bx, bz, ba, cx, cz, ca, uv = null, y = null) {
  const area = (bx - ax) * (cz - az) - (bz - az) * (cx - ax);
  if (area > -EPS && area < EPS) return;
  const u = uv ?? [0.5, 0.5, 0.5, 0.5, 0.5, 0.5];
  const yy = y == null ? null : typeof y === 'number' ? [y, y, y] : y;
  if (area > 0) {
    // Positive (CCW in xz) faces -Y — swap b and c.
    s.xz.push(ax, az, cx, cz, bx, bz);
    s.a.push(aa, ca, ba);
    s.uv.push(u[0], u[1], u[4], u[5], u[2], u[3]);
    if (yy) yChannel(s, s.a.length - 3).push(yy[0], yy[2], yy[1]);
    else if (s.y) s.y.push(NaN, NaN, NaN);
  } else {
    s.xz.push(ax, az, bx, bz, cx, cz);
    s.a.push(aa, ba, ca);
    s.uv.push(u[0], u[1], u[2], u[3], u[4], u[5]);
    if (yy) yChannel(s, s.a.length - 3).push(yy[0], yy[1], yy[2]);
    else if (s.y) s.y.push(NaN, NaN, NaN);
  }
}

/**
 * Triangulates a polygon into the soup at a constant alpha.
 * @param {DecalSoup} s
 * @param {Poly} poly
 * @param {number} alpha
 */
function pushPoly(s, poly, alpha) {
  if (!poly || poly.length < 3) return;
  for (const [i0, i1, i2] of triangulatePolygon(poly)) {
    const a = poly[i0];
    const b = poly[i1];
    const c = poly[i2];
    pushTri(s, a[0], a[1], alpha, b[0], b[1], alpha, c[0], c[1], alpha);
  }
}

/**
 * Ring of quads between two same-length polygons, alpha `aIn` on `inner` fading
 * to `aOut` on `outer`.
 * @param {DecalSoup} s
 * @param {Poly} inner @param {Poly} outer
 * @param {number} aIn @param {number} aOut
 */
function pushRing(s, inner, outer, aIn, aOut) {
  const n = Math.min(inner.length, outer.length);
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const p = inner[i];
    const q = inner[j];
    const r = outer[j];
    const t = outer[i];
    pushTri(s, p[0], p[1], aIn, q[0], q[1], aIn, r[0], r[1], aOut);
    pushTri(s, p[0], p[1], aIn, r[0], r[1], aOut, t[0], t[1], aOut);
  }
}

/**
 * The soft dark band that says "this object touches the ground here". Alpha is
 * `alpha` on the footprint edge and 0 at `width` metres out; the interior is
 * left empty because the object itself covers it.
 *
 * @param {Poly} poly Footprint, world XZ, either winding.
 * @param {{width?: number, alpha?: number}} [opts]
 * @returns {DecalSoup}
 */
export function buildContactRibbon(poly, opts = {}) {
  const width = opts.width ?? 1.6;
  const alpha = opts.alpha ?? 0.55;
  const s = emptySoup();
  if (!poly || poly.length < 3 || width <= 0 || alpha <= 0) return s;
  const outer = offsetPolygon(poly, width);
  if (outer.length !== poly.length) return s; // degenerate miter, skip quietly
  pushRing(s, poly, outer, alpha, 0);
  return s;
}

/**
 * The projected hard sun shadow of an extruded footprint.
 *
 * @param {Poly} poly Footprint, world XZ.
 * @param {number} height Extrusion height (metres above the ground plane).
 * @param {{x:number,y:number,z:number}|[number,number,number]} sunDir Normalised,
 *   surface -> sun. Must have `y > 0`.
 * @param {{alpha?: number, skirt?: number}} [opts] `skirt` = width of the soft
 *   fade band inset inside the hull.
 * @returns {DecalSoup}
 */
export function buildSunShadowPoly(poly, height, sunDir, opts = {}) {
  const alpha = opts.alpha ?? 0.42;
  const skirt = opts.skirt ?? 0.8;
  const s = emptySoup();
  if (!poly || poly.length < 3 || !(height > 0)) return s;

  const lx = Array.isArray(sunDir) ? sunDir[0] : sunDir.x;
  const ly = Array.isArray(sunDir) ? sunDir[1] : sunDir.y;
  const lz = Array.isArray(sunDir) ? sunDir[2] : sunDir.z;
  if (!(ly > 0.05)) return s; // sun on the horizon: the shadow is unbounded

  const kx = (-lx / ly) * height;
  const kz = (-lz / ly) * height;

  /** @type {Poly} */
  const pts = [];
  for (const p of poly) {
    pts.push([p[0], p[1]]);
    pts.push([p[0] + kx, p[1] + kz]);
  }
  const hull = convexHull2D(pts);
  if (hull.length < 3) return s;

  if (skirt > 0) {
    const inner = offsetPolygon(hull, -skirt);
    if (inner.length === hull.length) {
      pushPoly(s, inner, alpha);
      pushRing(s, inner, hull, alpha, 0);
      return s;
    }
  }
  // Footprint too small to hold a skirt: solid, slightly softened.
  pushPoly(s, hull, alpha * 0.8);
  return s;
}

// ─────────────────────────────────────────────────────────────────────────────
// Builder
// ─────────────────────────────────────────────────────────────────────────────

export class DecalBuilder {
  /**
   * @param {Object} [o]
   * @param {number} [o.y=0] Ground height for the FLAT path.
   * @param {number} [o.lift=0.015] Y lift on flat ground (NOT polygonOffset).
   * @param {number} [o.drape=0.02] Y lift above `heightAt` when draping.
   * @param {number} [o.maxEdge=1.5] Subdivision target when draping.
   * @param {THREE.Material|null} [o.material] Reuse a registry-owned material.
   *   When omitted, `build()` creates one with {@link decalMaterial} and the
   *   caller owns it (`registry.track(mesh.material)`).
   * @param {THREE.Texture|null} [o.map] Stencil for the WHOLE layer; its alpha
   *   multiplies the vertex alpha. Shadow/ribbon soups have no meaningful UVs,
   *   so a mapped layer should be a SEPARATE DecalBuilder from the shadow one.
   * @param {THREE.Color|number|string|[number,number,number]} [o.color=[0,0,0]]
   *   Ink colour, forwarded verbatim to {@link decalMaterial} — so a hex string
   *   from a level config works as well as a triple. Black is the shadow/wear
   *   default; a LIGHT layer (road paint) is what the other end of the range is
   *   for, and it needs its own builder because this colour is per LAYER.
   * @param {number} [o.nightFade=0.7] Alpha scale as `uNight` rises: no sun, no
   *   hard shadow.
   * @param {string} [o.name='decals']
   */
  constructor(o = {}) {
    this.y = o.y ?? 0;
    this.lift = o.lift ?? 0.015;
    this.drape = o.drape ?? 0.02;
    this.maxEdge = o.maxEdge ?? 1.5;
    this.material = o.material ?? null;
    this.map = o.map ?? null;
    this.color = o.color ?? [0, 0, 0];
    this.nightFade = o.nightFade ?? 0.7;
    this.name = o.name ?? 'decals';

    /** @type {DecalSoup} */
    this._soup = emptySoup();
    this._pieces = 0;
  }

  /** @returns {number} Decal pieces added (ribbons + shadows + quads). */
  get count() {
    return this._pieces;
  }

  /** @returns {number} Triangles accumulated so far. */
  get tris() {
    return this._soup.a.length / 3;
  }

  /**
   * @param {Poly} poly
   * @param {{width?: number, alpha?: number}} [opts]
   * @returns {this}
   */
  addRibbon(poly, opts) {
    return this.addSoup(buildContactRibbon(poly, opts));
  }

  /**
   * @param {Poly} poly
   * @param {number} height
   * @param {{x:number,y:number,z:number}|[number,number,number]} sunDir
   * @param {{alpha?: number, skirt?: number}} [opts]
   * @returns {this}
   */
  addShadow(poly, height, sunDir, opts) {
    return this.addSoup(buildSunShadowPoly(poly, height, sunDir, opts));
  }

  /**
   * Axis-aligned stencil quad — crosswalks, manhole covers, cracks, burn
   * patches.
   * @param {{x:number, z:number, w:number, d:number}} rect `x,z` is the MIN
   *   corner (the project-wide Rect convention, see build/polygon.js).
   * @param {{alpha?: number, uv?: {u0:number,v0:number,u1:number,v1:number}}} [opts]
   *   `uv` selects a sub-rect of `map` (atlas cell); defaults to the full 0..1.
   * @returns {this}
   */
  addQuad(rect, opts = {}) {
    const a = opts.alpha ?? 0.5;
    const uv = opts.uv ?? { u0: 0, v0: 0, u1: 1, v1: 1 };
    const x0 = rect.x;
    const z0 = rect.z;
    const x1 = rect.x + rect.w;
    const z1 = rect.z + rect.d;
    const s = this._soup;
    pushTri(s, x0, z0, a, x1, z0, a, x1, z1, a, [uv.u0, uv.v0, uv.u1, uv.v0, uv.u1, uv.v1]);
    pushTri(s, x0, z0, a, x1, z1, a, x0, z1, a, [uv.u0, uv.v0, uv.u1, uv.v1, uv.u0, uv.v1]);
    this._pieces++;
    return this;
  }

  /**
   * @param {DecalSoup} soup
   * @returns {this}
   */
  addSoup(soup) {
    if (!soup || soup.a.length === 0) return this;
    const s = this._soup;
    const base = s.a.length;
    for (let i = 0; i < soup.xz.length; i++) s.xz.push(soup.xz[i]);
    for (let i = 0; i < soup.a.length; i++) s.a.push(soup.a[i]);
    for (let i = 0; i < soup.uv.length; i++) s.uv.push(soup.uv[i]);
    // The optional per-vertex Y, in either direction: a soup that names one
    // opens the channel (back-filling NaN for everything already here), and a
    // soup that does not extends an open channel with NaN so the two stay in
    // step. A builder nobody hands a Y to never grows the array at all.
    if (soup.y) {
      const ch = yChannel(s, base);
      for (let i = 0; i < soup.y.length; i++) ch.push(soup.y[i]);
    } else if (s.y) {
      for (let i = 0; i < soup.a.length; i++) s.y.push(NaN);
    }
    this._pieces++;
    return this;
  }

  /**
   * Merges everything into ONE mesh.
   *
   * ─────────────────────────────────────────────────────────────────────────
   * PAINT THAT KNOWS ITS OWN HEIGHT
   * ─────────────────────────────────────────────────────────────────────────
   * A vertex pushed with an explicit `y` ({@link pushTri}) takes it verbatim
   * and is NOT draped. That exists because draping is only ever an
   * approximation of "on the surface I belong to": a country road's markings
   * belong on the ROAD, and the road is a ribbon floating `route.lift` metres
   * over a corridor the carve flattened to its own smoothed profile — not on
   * the heightfield the sampler reports. Draping them put 99% of the perimeter
   * ring's paint UNDER its own tarmac and floated other strokes up to 0.55 m
   * above it, which at the game's 52 degree pitch reads as a dash lying on the
   * grass beside the road. See `levels/city/countryroads.js#paintDashes`.
   *
   * A soup that names every one of its Y values also needs no subdivision: it
   * is already exactly where it belongs, and refining it to follow the ground
   * would pull it back off. So the drape decision is made over the vertices
   * that still have no opinion.
   *
   * @param {{heightAt?: ((x:number,z:number)=>number)|null}} [opts] Pass the
   *   level's sampler to drape over a heightfield. A sampler that turns out to
   *   be FLAT is detected and skips the subdivision entirely.
   * @returns {THREE.Mesh|null} `null` when nothing was added (no wasted draw).
   */
  build(opts = {}) {
    const soup = this._soup;
    const triCount = soup.a.length / 3;
    if (triCount === 0) return null;

    const heightAt = opts.heightAt ?? null;
    const ownY = soup.y ?? null;
    const needsGround = !ownY || ownY.some((v) => Number.isNaN(v));
    const drape = heightAt && needsGround ? !isFlat(heightAt, soup) : false;

    /** @type {DecalSoup} */
    const src = drape ? subdivide(soup, this.maxEdge) : soup;
    const n = src.a.length;
    const srcY = src.y ?? null;

    const pos = new Float32Array(n * 3);
    const alpha = new Float32Array(n);
    const uv = new Float32Array(n * 2);
    const flatY = this.y + this.lift;

    for (let i = 0; i < n; i++) {
      const x = src.xz[i * 2];
      const z = src.xz[i * 2 + 1];
      const own = srcY ? srcY[i] : NaN;
      pos[i * 3] = x;
      pos[i * 3 + 1] = Number.isNaN(own) ? (drape ? heightAt(x, z) + this.drape : flatY) : own;
      pos[i * 3 + 2] = z;
      alpha[i] = src.a[i];
      uv[i * 2] = src.uv[i * 2];
      uv[i * 2 + 1] = src.uv[i * 2 + 1];
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    // EXACTLY this name — decalMaterial() binds attribute('alpha','float').
    geo.setAttribute('alpha', new THREE.BufferAttribute(alpha, 1));
    geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    geo.computeBoundingBox();
    geo.computeBoundingSphere();

    const mat =
      this.material ??
      decalMaterial({
        map: this.map,
        color: this.color,
        nightFade: this.nightFade,
        name: this.name,
      });

    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = this.name;
    mesh.renderOrder = RENDER_ORDER.decal;
    mesh.userData.decalCount = this._pieces;
    return mesh;
  }

  /**
   * {@link DecalBuilder#build} wrapped in the `Level.decals` shape.
   * @param {{heightAt?: ((x:number,z:number)=>number)|null, parent?: THREE.Object3D|null}} [opts]
   * @returns {DecalLayer}
   */
  buildLayer(opts = {}) {
    const mesh = this.build(opts);
    if (mesh && opts.parent) opts.parent.add(mesh);
    const owned = mesh && !this.material ? mesh.material : null;
    return {
      mesh,
      count: this._pieces,
      dispose() {
        mesh?.geometry?.dispose();
        // Only dispose a material WE created; a registry-owned one is not ours.
        /** @type {any} */ (owned)?.dispose?.();
        mesh?.removeFromParent?.();
      },
    };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Draping
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {(x:number,z:number)=>number} heightAt
 * @param {DecalSoup} soup
 * @returns {boolean} True when the sampler is constant over the decal extent.
 */
function isFlat(heightAt, soup) {
  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < soup.xz.length; i += 2) {
    const x = soup.xz[i];
    const z = soup.xz[i + 1];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  const h0 = heightAt(minX, minZ);
  for (let j = 0; j <= 4; j++) {
    for (let i = 0; i <= 4; i++) {
      const x = minX + ((maxX - minX) * i) / 4;
      const z = minZ + ((maxZ - minZ) * j) / 4;
      if (Math.abs(heightAt(x, z) - h0) > 1e-4) return false;
    }
  }
  return true;
}

/**
 * 4-way triangle subdivision until every edge is <= `maxEdge`. Alpha and UV
 * interpolate linearly, so a fading skirt survives the split.
 * `maxEdge` is honoured up to `maxDepth` levels (1024 triangles from one input,
 * i.e. exact for anything under ~48 m at the 1.5 m default). Past that the cap
 * wins — a decal that big should have been split by its generator.
 *
 * @param {DecalSoup} soup
 * @param {number} maxEdge
 * @param {number} [maxDepth=5]
 * @returns {DecalSoup}
 */
function subdivide(soup, maxEdge, maxDepth = 5) {
  const out = emptySoup();
  const lim = maxEdge * maxEdge;
  const n = soup.a.length / 3;
  const y = soup.y ?? null;

  /**
   * @param {number[]} A @param {number[]} B @param {number[]} C
   *   Each is `[x, z, alpha, u, v, y]` — `y` is NaN wherever the vertex has no
   *   opinion, and NaN survives the midpoint average, which is what keeps a
   *   split triangle in one camp or the other.
   * @param {number} depth
   */
  const rec = (A, B, C, depth) => {
    const ab = (A[0] - B[0]) ** 2 + (A[1] - B[1]) ** 2;
    const bc = (B[0] - C[0]) ** 2 + (B[1] - C[1]) ** 2;
    const ca = (C[0] - A[0]) ** 2 + (C[1] - A[1]) ** 2;
    if (depth >= maxDepth || (ab <= lim && bc <= lim && ca <= lim)) {
      pushTri(
        out,
        A[0], A[1], A[2],
        B[0], B[1], B[2],
        C[0], C[1], C[2],
        [A[3], A[4], B[3], B[4], C[3], C[4]],
        y ? [A[5], B[5], C[5]] : null,
      );
      return;
    }
    const mid = (P, Q) => [
      (P[0] + Q[0]) * 0.5,
      (P[1] + Q[1]) * 0.5,
      (P[2] + Q[2]) * 0.5,
      (P[3] + Q[3]) * 0.5,
      (P[4] + Q[4]) * 0.5,
      (P[5] + Q[5]) * 0.5,
    ];
    const M0 = mid(A, B);
    const M1 = mid(B, C);
    const M2 = mid(C, A);
    rec(A, M0, M2, depth + 1);
    rec(M0, B, M1, depth + 1);
    rec(M2, M1, C, depth + 1);
    rec(M0, M1, M2, depth + 1);
  };

  for (let t = 0; t < n; t++) {
    const i = t * 3;
    /** @param {number} k @returns {number[]} */
    const v = (k) => [
      soup.xz[(i + k) * 2],
      soup.xz[(i + k) * 2 + 1],
      soup.a[i + k],
      soup.uv[(i + k) * 2],
      soup.uv[(i + k) * 2 + 1],
      y ? y[i + k] : NaN,
    ];
    rec(v(0), v(1), v(2), 0);
  }
  return out;
}

/**
 * Convenience: the four corners of a Rect as a polygon, for
 * `addRibbon`/`addShadow` on box-shaped props.
 * @param {{x:number,z:number,w:number,d:number}} rect
 * @returns {Poly}
 */
export const rectPoly = rectToPoly;
